import './UnAdministerMultiUsePage.css';
import Card from '../../card/Card';
import CheckBox from "../../checkBox/CheckBox";
import Content from '../../helpers/content/Content';
import DrugScanned from '../../popups/drugScanned/DrugScanned';
import Label from '../../label/Label';
import PrimaryButton from '../../primaryButton/PrimaryButton';
import React, { useState, useEffect } from 'react';
import SecondaryButton from "../../secondaryButton/SecondaryButton";
import Volume from '../../volume/Volume';
import mainAPI from '../../../api/MainAPI';
import toasts from '../../../utils/toasts';
import utils from '../../../utils/utils.js';
import { Page, Navbar } from 'framework7-react';
import getLocation, {getLastLocation} from "../../../utils/getLocation";
import throttle from 'lodash.throttle';
import dashboardController from "../../../controllers/dashboardController";

export const unAdministerMultiUsePageRoute = {name: 'un-administer', path: '/un-administer/v1/multi-use'};

const UnAdministerMultiUsePage = ({id: drug_id, drugCode} = {}) => {
	const [records, setRecords] = useState([]);
	const [selected, setSelected] = useState([]);
	const [drug, setDrug] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [isSuccessful, setSuccessful] = useState(null);

	const isSelected = ({id: target}) => {
		const record = selected.find(({id}) => id === target)
		if (record && typeof record.selected === 'boolean') return record.selected;
		return false
	}

	useEffect(() => {
		const fetchRecords = async () => {
      if(!drug_id) return;
			try {
				const drug = await mainAPI.drug.get(drug_id);
				const result = await mainAPI.stock_move_record.administeredForDrug(drug_id, {includeWasted: true}) || [];
				const updateSelected = result
					// Filter out records that have already been un-administered
					.filter(({un_administer_id}) => !!!un_administer_id)
					.map(({ id }) => ({id, selected: false}));
				setSelected(updateSelected);
				setRecords(result);
				setDrug(drug);
			} catch (error) {
				console.error('Error fetching stock move records and setting state.', error)
			}
		};
    getLocation()
		fetchRecords()
	}, [drug_id]);

	const select = (id, value) => {
		const copy = [...selected];
		const record = copy.find(({id: target}) => id === target);
		if (record) {
			record.selected = value;
			setSelected(copy);
		}
	}
	const renderRecord = ({id, created_on, type, quantity, created_by, witnessed_by, guest_verify_name, location_name, incident_number, un_administer_id, stock_move_type_name} = {}) => {
		const date = utils.display_datetime(created_on);
		const isUnAdministered = !!un_administer_id;
		const isAdminister = stock_move_type_name === 'administer';
		const undoLabel = isAdminister ? 'UN ADMINISTER' : 'UN WASTE';
		const undoneLabel = `Previously Un ${(isAdminister ? 'Administered' : 'Wasted')}`;
		return (
			<Card>
				<div key={'amount'} className="i-row">
					<Label className="label">AMOUNT</Label>
					<Label dark bold><Volume type={type} value={Math.abs(quantity)} /></Label>
				</div>
				<div key={'user'} className="i-row">
					<Label className="label">USER</Label>
					<Label dark bold>{created_by}</Label>
				</div>
				<div key={'witness'} className="i-row">
					<Label className="label">WITNESS</Label>
					<Label dark bold>{witnessed_by || guest_verify_name}</Label>
				</div>
				<div key={'date'} className="i-row">
					<Label className="label">DATE</Label>
					<Label dark bold>{date}</Label>
				</div>
				<div key={'incident'}  className="i-row">
					<Label className="label">INCIDENT</Label>
					<Label dark bold>{incident_number}</Label>
				</div>
				{Boolean(location_name) &&
				<div key={'location'} className="i-row location">
					<Label dark bold>{location_name}</Label>
				</div>}
				<div key={'un-administer'} className="i-row un-administer">
					<Label className="label">{undoLabel}</Label>
					{isUnAdministered ?
						<Label dark bold>{undoneLabel}</Label> :
						<CheckBox value={isSelected({id})}
								  onChange={(value) => select(id, value)}/>
					}
				</div>
			</Card>
		);
	}

	const unAdminister = throttle(async () => {
		if(isSuccessful) {
			return; // prevent duplicate request
		}
		if (isProcessing) {
			return toasts.show('Processing request');
		}

		const ids = selected
			.filter(({selected}) => selected)
			.map(({id}) => id);

		if (ids.length === 0) {
			toasts.error('Please select at least one record to reverse.');
			return;
		}

		setIsProcessing(true);

		try {
			const candidates = records.filter(({ id }) => ids.includes(id));
			const results = await Promise.all(candidates.map(unAdministerRecord));
			
			if (results.some(({error}) => !!error)) {
				toasts.error('There was an error reversing the selected records.');
				setSuccessful(false);
			} else {
				setSuccessful(true);
				dashboardController.refresh();
				window.popupController.hide(DrugScanned);
			}
		} catch (e) {
			setSuccessful(false);
			console.error('Error in unAdminister:', e);
			toasts.error('There was an error reversing the selected records.');
		} finally {
			setIsProcessing(false);
		}
	}, 900, { leading: true, trailing: false }); // allow first click but ignore others until after 900 ms.

	const unAdministerRecord = async ({id: source_id, quantity}) => {
		const {lat, lon} = getLastLocation();
		const data = {
			stock_move_type_id__name: 'un_administer',
			drug_id: drug_id,
			doses: 0,
			quantity: Math.abs(quantity),
			source_id,
			lat,
			lon
		};
		const result = await mainAPI.stock_move_record.create(data);
		if (!result) {
			mainAPI.error_log.create({
				type: 'UnAdministerMultiUsePage Failed',
				message: `Failed to create un-administer record. ${JSON.stringify(data)}`
			});
			return {error: `Failed to create un-administer record. source_id: ${source_id}, drug_id: ${drug_id}, quantity: ${quantity}`}
		}
		dashboardController.refresh(drug?.drug_box_id)
		return result
	}

	const cancelButton = (isProcessing) => (
		<SecondaryButton disabled={isProcessing} center onClick={() => {
			window.f7.views.current.router.back();
		}}>
			Cancel
		</SecondaryButton>
	)

	return (
		<Page className="g-un-administer-multi-use-page">
			<Navbar backLink="Back" title={drugCode} />
			<Content>
				{records.length ? (
					<>
						{records.map(renderRecord)}
						<PrimaryButton 
							center 
							onClick={unAdminister}
							disabled={isProcessing}
						>
							{isProcessing ? 'Processing...' : 'Reverse Selected Records'}
						</PrimaryButton>
						{cancelButton(isProcessing)}
					</>) : (
					<>
						<Label large dark center>No Administrations</Label>
						{cancelButton(isProcessing)}
					</>
				)}
			</Content>
		</Page>
	);
}
export default UnAdministerMultiUsePage;
